import { SEO } from "@/components/SEO";
import { PageSchema } from "@/components/seo/PageSchema";
import { LastUpdated } from "@/components/seo/LastUpdated";
import { TopCTA } from "@/components/cta/TopCTA";
import { MidPageCTA } from "@/components/cta/MidPageCTA";
import { BottomCTA } from "@/components/cta/BottomCTA";
import { ComparisonTable } from "@/components/content/ComparisonTable";
import { VerdictBox } from "@/components/content/VerdictBox";
import { FAQSection } from "@/components/content/FAQSection";
import { InternalLinks } from "@/components/content/InternalLinks";
import { LeadFormSection } from "@/components/LeadFormSection";
import { NavLink } from "@/components/NavLink";

const SquarespaceVsWordpressLandscaping = () => {
  const comparisonData = [
    { feature: "Monthly Cost", optionA: "$16–$49/mo", optionB: "$3–$50/mo + hosting" },
    { feature: "SEO Capabilities", optionA: "Good basics", optionB: "Advanced & unlimited" },
    { feature: "Design Quality", optionA: "Beautiful templates", optionB: "Unlimited customization" },
    { feature: "Page Speed", optionA: "Generally fast", optionB: "Depends on optimization" },
    { feature: "Portfolio Features", optionA: "Excellent built-in", optionB: "Via plugins" },
    { feature: "E-commerce", optionA: "Limited", optionB: "WooCommerce (powerful)" },
    { feature: "Blogging", optionA: "Good", optionB: "Industry standard" },
    { feature: "Custom Code", optionA: "Limited", optionB: "Full access" },
  ];

  const verdicts = [
    { label: "Visual Portfolio", recommendation: "Squarespace" },
    { label: "SEO Growth", recommendation: "WordPress" },
    { label: "Budget", recommendation: "WordPress (long-term)" },
    { label: "Ease of Use", recommendation: "Squarespace" },
    { label: "Scalability", recommendation: "WordPress" },
  ];

  const faqs = [
    {
      question: "Is Squarespace good for landscaping websites?",
      answer: "Squarespace excels at visual presentation, making it suitable for showcasing landscaping portfolios. However, its SEO limitations can hinder organic lead generation compared to WordPress."
    },
    {
      question: "Which platform is better for showcasing landscaping projects?",
      answer: "Squarespace has beautiful built-in gallery features. WordPress can match this with plugins but requires more setup. For pure visual portfolios, Squarespace has an edge."
    },
    {
      question: "Can Squarespace rank on Google for landscaping keywords?",
      answer: "Yes, but with limitations. Squarespace lacks advanced SEO plugins, schema markup control, and some technical optimization options that WordPress offers for competitive keywords."
    },
    {
      question: "How much does each platform cost for landscapers?",
      answer: "Squarespace runs $16–$49/month with everything included. WordPress hosting costs $3–$30/month, plus themes ($0–$200) and plugins ($0–$300/year). Professional builds add $2,000–$10,000."
    },
    {
      question: "Which is faster—Squarespace or WordPress?",
      answer: "Out of the box, Squarespace is often faster. However, a properly optimized WordPress site can achieve better speeds, which directly impacts Google rankings and conversions."
    },
    {
      question: "Can I move from Squarespace to WordPress?",
      answer: "Yes, but it requires rebuilding. Squarespace allows limited content export. Plan for a complete redesign if you switch platforms later."
    },
    {
      question: "Which platform handles service area pages better?",
      answer: "WordPress offers more flexibility for creating and optimizing service area pages with plugins like Rank Math. This is crucial for landscapers serving multiple cities."
    },
    {
      question: "Is WordPress too technical for landscaping business owners?",
      answer: "Modern WordPress with page builders is quite user-friendly. However, for maximum results without the hassle, a done-for-you website subscription handles everything."
    },
    {
      question: "Which platform is better for getting landscaping leads?",
      answer: "WordPress typically generates more leads due to superior SEO capabilities and form functionality. However, execution matters more than platform choice."
    },
    {
      question: "Should I hire someone to build my landscaping website?",
      answer: "For best results, yes. A professional understands conversion optimization, local SEO, and industry best practices that DIY builders often miss."
    },
  ];

  return (
    <>
      <SEO 
        title="Squarespace vs WordPress for Landscaping Sites | Leaf Rush"
        description="Squarespace vs WordPress for landscaping websites: Compare design, SEO, cost, and lead generation capabilities. Find the best platform for your business."
        canonical="https://www.leafrushmarketing.com/comparisons/squarespace-vs-wordpress-landscaping/"
      />
      <PageSchema 
        title="Squarespace vs WordPress for Landscaping Websites"
        description="Squarespace vs WordPress for landscaping websites: Compare design, SEO, cost, and lead generation capabilities."
        url="https://www.leafrushmarketing.com/comparisons/squarespace-vs-wordpress-landscaping/"
        faqs={faqs}
      />
      
      <article className="py-16">
        <div className="container px-4 md:px-6">
          <LastUpdated />
          
          <header className="max-w-4xl mx-auto text-center mb-12">
            <h1 className="text-4xl md:text-5xl font-bold mb-6">
              Squarespace vs WordPress for Landscaping Websites
            </h1>
            <p className="text-xl text-muted-foreground mb-8">
              Beautiful templates or unlimited flexibility? Compare the two most popular platforms for landscaping business websites.
            </p>
          </header>

          <TopCTA />

          <div className="max-w-4xl mx-auto">
            <VerdictBox verdicts={verdicts} />

            <section className="mb-12">
              <h2 className="text-2xl font-bold mb-6">Platform Comparison</h2>
              <p className="text-muted-foreground mb-6">
                Your <NavLink to="/landscaping-website-design/" className="text-primary hover:underline">landscaping website design</NavLink> choice 
                impacts how well you can showcase projects and attract new clients.
              </p>
              <ComparisonTable 
                optionAName="Squarespace"
                optionBName="WordPress"
                rows={comparisonData}
              />
            </section>

            <section className="mb-12">
              <h2 className="text-2xl font-bold mb-6">Who Should Choose Squarespace</h2>
              <ul className="space-y-3">
                <li className="flex items-start gap-3">
                  <span className="text-primary font-bold">✓</span>
                  <span>Landscapers who prioritize stunning visual portfolios above all else</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-primary font-bold">✓</span>
                  <span>Those who want an all-in-one solution without plugin management</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-primary font-bold">✓</span>
                  <span>Businesses with minimal technical knowledge and limited time</span>
                </li>
              </ul>
            </section>

            <section className="mb-12">
              <h2 className="text-2xl font-bold mb-6">Who Should Choose WordPress</h2>
              <ul className="space-y-3">
                <li className="flex items-start gap-3">
                  <span className="text-primary font-bold">✓</span>
                  <span>Landscaping companies focused on <NavLink to="/landscaping-website-seo/" className="text-primary hover:underline">SEO and organic traffic</NavLink></span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-primary font-bold">✓</span>
                  <span>Businesses wanting to build <NavLink to="/service-area-pages-local-seo/" className="text-primary hover:underline">multiple service area pages</NavLink></span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-primary font-bold">✓</span>
                  <span>Those planning to scale with advanced <NavLink to="/landscaping-quote-form/" className="text-primary hover:underline">quote form features</NavLink></span>
                </li>
              </ul>
            </section>

            <MidPageCTA />

            <section className="mb-12">
              <h2 className="text-2xl font-bold mb-6">The Best Option: Professional Done-For-You</h2>
              <p className="text-muted-foreground mb-4">
                Why choose between platforms when you can have experts handle everything? Our 
                <NavLink to="/websites/" className="text-primary hover:underline"> website subscription service</NavLink> builds 
                conversion-optimized landscaping websites without the DIY learning curve.
              </p>
              <p className="text-muted-foreground mb-4">
                We pair beautiful design with powerful <NavLink to="/service-pages-that-rank/" className="text-primary hover:underline">SEO-optimized service pages</NavLink>, 
                ensuring your landscaping business gets found AND converts visitors into leads.
              </p>
              <p className="text-muted-foreground">
                Add <NavLink to="/growth-add-ons/gbp/" className="text-primary hover:underline">Google Business Profile optimization</NavLink> for 
                complete local visibility, or <NavLink to="/growth-add-ons/google-ads/" className="text-primary hover:underline">Google Ads</NavLink> for 
                immediate lead flow.
              </p>
            </section>

            <FAQSection faqs={faqs} />

            <InternalLinks 
              links={[
                { href: "/comparisons/wix-vs-wordpress-lawn-care/", text: "Wix vs WordPress Comparison" },
                { href: "/comparisons/best-website-builder-for-landscapers/", text: "Best Builders for Landscapers" },
                { href: "/landscaping-website-cost/", text: "Landscaping Website Cost Guide" },
                { href: "/landscaping-website-examples/", text: "Landscaping Website Examples" },
              ]}
            />
          </div>
        </div>
      </article>

      <BottomCTA />
      
      <LeadFormSection title="Schedule Your Free Consultation" />
    </>
  );
};

export default SquarespaceVsWordpressLandscaping;
